import { useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Key, User, Warning, PencilSimple } from "@phosphor-icons/react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");

  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileOk, setProfileOk] = useState(false);
  const [profileError, setProfileError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOk(false);
    if (newPw.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("New passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setOk(true);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditProfile = () => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setProfileError("");
    setProfileOk(false);
    setEditingProfile(true);
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileOk(false);
    if (!firstName.trim() || !lastName.trim()) {
      setProfileError("First name and last name are both required.");
      return;
    }
    setProfileSubmitting(true);
    try {
      await api.patch("/users/me/profile", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      if (refresh) await refresh();
      setProfileOk(true);
      setEditingProfile(false);
    } catch (err) {
      setProfileError(getErrorMessage(err));
    } finally {
      setProfileSubmitting(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
          Your account
        </div>
        <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none">
          Settings
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile card */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6 h-fit" data-testid="profile-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <User size={16} weight="bold" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Profile</div>
            </div>
            {!editingProfile && (
              <button
                type="button"
                onClick={startEditProfile}
                data-testid="edit-profile-btn"
                className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950"
              >
                <PencilSimple size={12} weight="bold" /> Edit
              </button>
            )}
          </div>

          {editingProfile ? (
            <form onSubmit={submitProfile} className="space-y-3" data-testid="edit-profile-form">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">First name</div>
                <input
                  data-testid="edit-first-name-input"
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Last name</div>
                <input
                  data-testid="edit-last-name-input"
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                />
              </div>
              {profileError && (
                <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-xs px-2 py-1.5 rounded-sm" data-testid="edit-profile-error">
                  {profileError}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEditingProfile(false)}
                  className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-2 rounded-sm text-xs hover:bg-zinc-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={profileSubmitting || !firstName.trim() || !lastName.trim()}
                  data-testid="save-profile-btn"
                  className="flex-1 bg-zinc-950 text-white py-2 rounded-sm text-xs hover:bg-zinc-800 disabled:opacity-50"
                >
                  {profileSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">First name</div>
                <div className="text-sm font-medium" data-testid="profile-first-name">{user?.first_name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Last name</div>
                <div className="text-sm font-medium" data-testid="profile-last-name">{user?.last_name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Email</div>
                <div className="text-sm font-medium break-all">{user?.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Role</div>
                <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${
                  user?.role === "admin" ? "bg-zinc-950 text-white" : "border border-zinc-200 text-zinc-600"
                }`}>
                  {user?.role || "user"}
                </span>
              </div>
              {profileOk && (
                <div className="border border-emerald-500 bg-emerald-50 text-emerald-900 text-xs px-2 py-1.5 rounded-sm flex items-center gap-1.5" data-testid="profile-saved">
                  <CheckCircle size={12} weight="fill" /> Name updated
                </div>
              )}
            </div>
          )}
        </div>

        {/* Change password */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-6">
            <Key size={16} weight="bold" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Change password</div>
          </div>

          <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Current password
              </label>
              <input
                data-testid="current-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="Enter your current password"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                New password
              </label>
              <input
                data-testid="new-password-input"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Confirm new password
              </label>
              <input
                data-testid="confirm-password-input"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="Retype the new password"
              />
            </div>

            {ok && (
              <div className="border border-emerald-500 bg-emerald-50 text-emerald-900 text-sm px-3 py-2.5 rounded-sm flex items-center gap-2" data-testid="change-password-success">
                <CheckCircle size={14} weight="fill" /> Password updated. Next login will use it.
              </div>
            )}
            {error && (
              <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2.5 rounded-sm flex items-center gap-2" data-testid="change-password-error">
                <Warning size={14} weight="bold" /> {error}
              </div>
            )}

            <button
              data-testid="change-password-submit"
              type="submit"
              disabled={submitting || !currentPw || !newPw}
              className="bg-zinc-950 text-white px-5 py-2.5 rounded-sm font-medium hover:bg-zinc-800 transition-colors text-sm disabled:opacity-50"
            >
              {submitting ? "Updating..." : "Update password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
